import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';

import { toast } from 'react-toastify';

import api from '../../lib/api';

/**
 * `options.enabled` was accepted by callers and silently ignored, so the create
 * page — which has no id — still issued `GET /organization/products/` on mount.
 * An empty id now disables the query outright, as the call sites always assumed.
 */
export function useGetProduct(
  id: string,
  options?: {
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: ['product', id],

    queryFn: async () => (await api.get(`/organization/products/${id}`)).data,

    placeholderData: keepPreviousData,

    enabled: Boolean(id) && (options?.enabled ?? true),
  });
}

export type UpdateProductDetailsPayload = {
  name?: string;

  /**
   * undefined = leave the saved SKU unchanged.
   * null / "" = clear the saved SKU.
   */
  sku?: string | null;

  description?: string;

  deliveryType?: 'BULK' | 'PALLET';

  image?: File | null;

  removeImage?: boolean;

  trackInventory?: boolean;

  defaultLocationId?: string | null;
};

export function useUpdateProductDetails(id: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateProductDetailsPayload) => {
      const fd = new FormData();

      if (payload.name != null) {
        fd.append('name', payload.name);
      }

      /*
       * Important:
       *
       * undefined = don't touch SKU
       * "" / null = explicitly clear SKU
       *
       * buildDetailsPayload() sends the canonical
       * string value, including "" when an existing
       * SKU is removed.
       */
      if (payload.sku !== undefined) {
        fd.append('sku', payload.sku ?? '');
      }

      if (payload.description !== undefined) {
        fd.append('description', payload.description ?? '');
      }

      if (payload.deliveryType) {
        fd.append('deliveryType', payload.deliveryType);
      }

      if (payload.image instanceof File) {
        fd.append('image', payload.image);
      }

      if (payload.removeImage) {
        fd.append('removeImage', 'true');
      }

      if (payload.trackInventory !== undefined) {
        fd.append('trackInventory', String(payload.trackInventory));
      }

      if (payload.defaultLocationId !== undefined) {
        fd.append('defaultLocationId', payload.defaultLocationId ?? '');
      }

      const { data } = await api.patch(`/organization/products/${id}`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return data;
    },

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['product', id],
      });

      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],
        exact: false,
      });
    },
  });
}

export function useReplacePalletSizes(id: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      rows: Array<{
        id?: string;

        name?: string;

        type?: string;

        lengthIn: number;

        widthIn: number;

        heightIn: number;

        weightTons?: number | null;
      }>
    ) => (await api.put(`/organization/products/${id}/pallet-sizes`, rows)).data,

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['product', id],
      });

      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],
        exact: false,
      });
    },
  });
}

export function useReplaceLoadOptions(id: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      items: Array<{
        id?: string;

        name: string;

        approximateLoadTons: number;

        active: boolean;

        prices: Array<{
          locationId: string;

          fullLoadPrice: number;
        }>;
      }>
    ) => (await api.put(`/organization/products/${id}/load-options`, items)).data,

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['product', id],
      });

      qc.invalidateQueries({
        queryKey: ['products'],
      });
    },
  });
}

export type ReplaceProductStockInput = {
  locationId: string;

  quantity: number;

  /**
   * Southern pickup / stone price in cents.
   *
   * null = unpriced
   * 0 is a valid explicit value.
   */
  stonePriceCents: number | null;
};

export function useReplaceStocks(productId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (stocks: ReplaceProductStockInput[]) => {
      const response = await api.post(`/organization/products/${productId}/stocks`, stocks);

      return response.data;
    },

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['product', productId],
      });

      qc.invalidateQueries({
        queryKey: ['products'],
      });

      qc.invalidateQueries({
        queryKey: ['auditLogs'],
        exact: false,
      });
    },

    onError: (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const locations = Array.isArray(error.response.data?.locations)
          ? error.response.data.locations
          : [];

        toast.error(
          locations.length
            ? 'Cannot remove stock: there are reserved items at some locations.'
            : 'Cannot remove stock due to reservations.'
        );

        return;
      }

      toast.error('Failed to save warehouses.');
    },
  });
}
